import { Injectable, SimpleChanges } from '@angular/core';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { UtilService } from '@service/util/util.service';
import {
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	SUBDIVISION_LOOKUP_KEY,
} from 'cargo-chief-commons';

// 'B.C.S.' -> 'BCS', 'Q. Roo' -> 'QROO'. Google punctuates its Mexican
// subdivision short_names; nothing else in the app does.
//
// Accents are folded before the strip, not stripped with the punctuation. Google's
// short name for the state of Mexico is 'Mex.' with an accent on the e, and
// removing the character instead of simplifying it leaves 'MX', which matches
// nothing -- so the state holding Toluca, Ecatepec and Cuautitlan read as no
// country at all.
//
// The rule itself lives with the canonical table rather than being written twice:
// the two decide the same question about the same values and must not drift.
const NORMALIZE_SUBDIVISION = SUBDIVISION_LOOKUP_KEY;

// CN-8810: a lane is Canadian if its origin or destination is in Canada. We
// prefer the location's country code and fall back to the province/state code
// (the search form does not always carry a country).
const CANADA_COUNTRY_CODES = new Set(['CA', 'CAN', 'CANADA'].map(NORMALIZE_SUBDIVISION));
const CANADIAN_PROVINCES = new Set([
	'AB',
	'BC',
	'MB',
	'NB',
	'NL',
	'NS',
	'NT',
	'NU',
	'ON',
	'PE',
	'QC',
	'SK',
	'YT',
]);

// CN-8851: same shape for Mexico. Canada is checked first, which is what keeps
// the two ambiguous codes below resolving the way they always have.
// Folded through NORMALIZE_SUBDIVISION like the states are, and carrying the same
// spellings MEXICO_COUNTRY_KEYS in the canonical table accepts. The two have to
// agree: a lane whose state that table canonicalises as Mexican must be judged
// Mexican here, or the screen prices a lane the API refuses.
const MEXICO_COUNTRY_CODES = new Set(
	['MX', 'MEX', 'Mexico', 'United Mexican States', 'Estados Unidos Mexicanos'].map(
		NORMALIZE_SUBDIVISION
	)
);

// Both the ISO 3166-2:MX codes and Google's own abbreviations, because the two
// paths into this service disagree: capacity search carries Google's
// administrative_area_level_1 short_name ('Jal.', 'Tamps.', 'N.L.'), while
// project details carries whatever the TMS stored. Compared after
// NORMALIZE_SUBDIVISION strips punctuation, so 'B.C.S.' and 'BCS' both land here.
//
// The two-letter row is the scheme our TMS feeds write, which is neither ISO nor
// Google's short names: 'CI' for Chihuahua, 'NX' for Nuevo Leon, 'EM' for the
// state of Mexico, 'GJ' and 'GT' for Guanajuato. The project-details path carries
// whatever the TMS stored, so these arrive here. Without them such a lane matched
// neither this set nor the Canadian one, was identified as nothing, and the
// pricing call went ahead as though it were domestic.
//
// Deliberately absent: 'BC' (Baja California) and 'NL' (Nuevo Leon). Both collide
// with a Canadian province -- British Columbia and Newfoundland and Labrador --
// and a bare state code cannot tell them apart. They stay Canadian, as they are
// today. Only the message is wrong, and only on the project-details path where
// no country is set; both countries skip the pricing call either way. The fix is
// a country on the project DTO, not a longer list here. That reasoning does not
// extend to the codes above: those were caught by neither list.
//
// Also absent, and for a different reason: 'CU'. It was kept out of the server
// tables on the evidence that its recorded endpoints scatter too widely to name a
// state, and adding it here would start treating unidentifiable lanes as Mexican.
//
// Absent and MUST STAY absent, which the two notes above do not cover because
// these are the dangerous ones: 'CO' (Coahuila), 'MI' (Michoacan) and 'MO'
// (Morelos). All three belong to the same two-letter scheme as the codes above,
// and all three are US states -- Colorado, Michigan and Missouri. Membership here
// stops a lane being priced, so completing the row from a standard table of
// Mexican abbreviations would silently stop pricing three US states.
//
// The row is the scheme as the server tables record it, each entry decoded from
// the cities and coordinates seen against it in production rather than from what
// the abbreviation looks like. Codes that scheme does not evidence are left out
// on purpose; adding one is a change to make on all of the tables at once.
const MEXICAN_STATES = new Set([
	'AGU', 'AGS', 'AG',
	'BCN', 'BJ', 'TJ',
	'BCS',
	'CAM', 'CAMP', 'CM',
	'CHP', 'CHIS',
	'CHH', 'CHIH', 'CH', 'CI',
	'CMX', 'CDMX', 'DF',
	'COA', 'COAH',
	'COL',
	'DUR', 'DGO', 'DG',
	'GUA', 'GTO', 'GJ', 'GT',
	'GRO',
	'HID', 'HGO', 'HG',
	'JAL', 'JA',
	'MEX', 'EDOMEX', 'EM',
	'MIC', 'MICH',
	'MOR',
	'NAY',
	'NLE', 'NX',
	'OAX',
	'PUE', 'PU',
	'QUE', 'QRO', 'QA', 'QT', 'QE',
	'ROO', 'QR', 'QROO',
	'SLP', 'SL',
	'SIN', 'SI',
	'SON', 'SO',
	'TAB',
	'TAM', 'TAMPS', 'TM',
	'TLA', 'TLAX', 'TL',
	'VER',
	'YUC',
	'ZAC',
]);

@Injectable()
export class PricingHelperService {
	constructor(
		private utilService: UtilService,
		private cargochief: Cargochief,
		private segmentWrapper: SegmentWrapperService
	) {}

	emailSentSuccessfully() {
		this.utilService.showSnackbar(
			'You are on the list! You will receive an email confirmation along with some follow up information.',
			SNACKBAR.SUCCESS,
			SNACKBAR.ACTION
		);
	}

	fireSegmentEvent(eventName: string) {
		const eventProperties = {
			user: this.cargochief.getUser(),
		};
		this.segmentWrapper.fireSegmentEvent(eventName, eventProperties);
	}

	hasSearchChanged(previousSearch, currentSearch): boolean {
		let result = false;
		if (IS_NULL_OR_UNDEFINED(previousSearch) || IS_NULL_OR_UNDEFINED(currentSearch)) {
			result = true;
		} else {
			if (
				previousSearch['origin']['city'] !== currentSearch['origin']['city'] ||
				previousSearch['origin']['state'] !== currentSearch['origin']['state'] ||
				previousSearch['origin']['zip'] !== currentSearch['origin']['zip'] ||
				// CN-8851: country decides whether pricing is skipped, so a change to it
				// alone is a changed search. Compared normalised -- the paths in disagree
				// on how they spell "no country" ('' from a cleared autocomplete, null
				// from a URL round-trip, undefined from project details), and a raw !==
				// would call those three different and re-price the same lane.
				this.countryOf(previousSearch['origin']) !== this.countryOf(currentSearch['origin']) ||
				this.countryOf(previousSearch['destination']) !==
					this.countryOf(currentSearch['destination']) ||
				previousSearch['destination']['city'] !== currentSearch['destination']['city'] ||
				previousSearch['destination']['state'] !== currentSearch['destination']['state'] ||
				previousSearch['destination']['zip'] !== currentSearch['destination']['zip'] ||
				previousSearch['trailerType'] !== currentSearch['trailerType'] ||
				previousSearch['pickupDateStart'] !== currentSearch['pickupDateStart'] ||
				previousSearch['pickupDateEnd'] !== currentSearch['pickupDateEnd'] ||
				previousSearch['hazmat'] !== currentSearch['hazmat']
			) {
				result = true;
			}
		}
		return result;
	}

	isCitySelected(currentSearch: Object): boolean {
		return (
			IS_NOT_EMPTY(currentSearch['origin']['city']) &&
			IS_NOT_EMPTY(currentSearch['destination']['city'])
		);
	}

	isEquipmentSelected(currentSearch: Object): boolean {
		return IS_NOT_EMPTY(currentSearch['trailerType']);
	}

	isCanadianLane(currentSearch: any): boolean {
		if (IS_NULL_OR_UNDEFINED(currentSearch)) {
			return false;
		}
		return (
			this.isCanadianLocation(currentSearch['origin']) ||
			this.isCanadianLocation(currentSearch['destination'])
		);
	}

	// '' when the location has no country, whatever shape "no country" arrived in.
	private countryOf(location: any): string {
		if (IS_NULL_OR_UNDEFINED(location) || !IS_NOT_EMPTY(location['country'])) {
			return '';
		}
		// Folded like the subdivision. A country that is present is taken as the
		// answer without consulting the state, so a spelling we do not know does not
		// merely fail to match -- it stops the lane falling back, and it is priced
		// as domestic.
		return NORMALIZE_SUBDIVISION(location['country']);
	}

	private isCanadianLocation(location: any): boolean {
		if (IS_NULL_OR_UNDEFINED(location)) {
			return false;
		}
		const country = this.countryOf(location);
		// When we know the country, trust it (a US lane must never be treated as
		// Canadian just because a province-like code slipped into state).
		if (country) {
			return CANADA_COUNTRY_CODES.has(country);
		}
		// Same normalisation as the Mexican twin below. A TMS-written 'B.C.' matched
		// neither list before -- punctuation intact here, and BC deliberately absent
		// from the Mexican one -- so the lane priced as domestic.
		const state = IS_NOT_EMPTY(location['state'])
			? NORMALIZE_SUBDIVISION(location['state'])
			: '';
		return CANADIAN_PROVINCES.has(state);
	}

	isMexicanLane(currentSearch: any): boolean {
		if (IS_NULL_OR_UNDEFINED(currentSearch)) {
			return false;
		}
		return (
			this.isMexicanLocation(currentSearch['origin']) ||
			this.isMexicanLocation(currentSearch['destination'])
		);
	}

	private isMexicanLocation(location: any): boolean {
		if (IS_NULL_OR_UNDEFINED(location)) {
			return false;
		}
		const country = this.countryOf(location);
		// When we know the country, trust it (a US lane must never be treated as
		// Mexican just because a state-like code slipped into state).
		if (country) {
			return MEXICO_COUNTRY_CODES.has(country);
		}
		const state = IS_NOT_EMPTY(location['state'])
			? NORMALIZE_SUBDIVISION(location['state'])
			: '';
		return MEXICAN_STATES.has(state);
	}

	greenScreensEnabledValueChanged(changes: SimpleChanges): boolean {
		return this.integrationEnabledValueChanged(changes, 'greenScreensEnabled');
	}

	sonarEnabledValueChanged(changes: SimpleChanges): boolean {
		return this.integrationEnabledValueChanged(changes, 'sonarEnabled');
	}

	private integrationEnabledValueChanged(changes: SimpleChanges, input: string): boolean {
		const previousValue = changes?.[input]?.previousValue;
		const currentValue = changes?.[input]?.currentValue;
		return (
			IS_NOT_NULL_OR_UNDEFINED(previousValue) &&
			IS_NOT_NULL_OR_UNDEFINED(currentValue) &&
			previousValue != currentValue
		);
	}

	retainSingleEquipment(searchTruckForm: any) {
		return {
			...searchTruckForm,
			trailerType: searchTruckForm['trailerType'].split(',')[0],
		};
	}
}
